package org.example.project.enginevertrieb

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.put
import kotlinx.serialization.json.add
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.decodeFromJsonElement
import org.example.project.enginetabular.loadWithTabularFilters
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit

object VertriebsindexApi {
    private const val API = "vertriebsindex"

    private val json = Json {
        ignoreUnknownKeys = true
        // `false` ist bei den Recherche-Checkboxen ein fachlicher Wert und darf
        // beim Speichern nicht als Kotlin-Standardwert aus dem JSON verschwinden.
        encodeDefaults = true
    }

    @Serializable
    private data class StatusAntwort(val id: String, val status: String)

    @Serializable
    data class ZusatzHeader(val id: String, val label: String, val typ: String = "TEXT")

    data class NeuerHeader(val label: String, val typ: String)


    @Serializable
    private data class ScriptEintrag(val id: String = "", val typ: String, val inhalt: String)

    private fun ScriptEintrag.asAll() = All(id = id, scriptTyp = typ, scriptInhalt = inhalt)

    data class ScriptAenderung(val sourceId: String, val sourceTyp: String, val typ: String, val inhalt: String)

    private fun scriptAction(action: String, bereich: String): String {
        require(bereich in setOf("CALL", "TERMIN"))
        return "$API/${if (bereich == "TERMIN") "termin-" else ""}script-$action"
    }

    suspend fun loadScriptIndex(bereich: String = "CALL"): List<All> =
        json.decodeFromString(ListSerializer(ScriptEintrag.serializer()), LumaticaApiManager.call(scriptAction("index", bereich)))
            .map { it.asAll() }

    suspend fun saveScript(eintrag: All, bereich: String = "CALL"): All =
        json.decodeFromString(ScriptEintrag.serializer(), postJson(scriptAction("save", bereich), buildJsonObject {
            put("id", eintrag.id); put("typ", eintrag.scriptTyp); put("inhalt", eintrag.scriptInhalt)
        }.toString())).asAll()

    suspend fun saveScripts(eintraege: List<ScriptAenderung>, bereich: String = "CALL"): List<All> =
        json.decodeFromString(ListSerializer(ScriptEintrag.serializer()), postJson(scriptAction("save-batch", bereich), buildJsonObject {
            put("eintraege", buildJsonArray { eintraege.forEach { item -> add(buildJsonObject {
                put("sourceId", item.sourceId); put("sourceTyp", item.sourceTyp)
                put("typ", item.typ); put("inhalt", item.inhalt)
            }) } })
        }.toString())).map { it.asAll() }

    suspend fun deleteScript(eintrag: All, bereich: String = "CALL") {
        postJson(scriptAction("delete", bereich), buildJsonObject {
            put("id", eintrag.id); put("typ", eintrag.scriptTyp)
        }.toString())
    }

    suspend fun deleteScripts(eintraege: List<All>, bereich: String = "CALL") {
        postJson(scriptAction("delete-batch", bereich), buildJsonObject {
            put("eintraege", buildJsonArray { eintraege.forEach { item -> add(buildJsonObject {
                put("id", item.id); put("typ", item.scriptTyp)
            }) } })
        }.toString())
    }

    @Serializable
    data class LeadTypEintrag(val id: String, val leadTyp: String, val flowProfileIds: Map<String, String> = emptyMap())

    @Serializable
    data class IcpEintrag(val id: String, val icp: String)


    @Serializable
    data class CsvImportErgebnis(val imported: Int, val duplicates: Int, val skipped: Int)

    suspend fun loadHeaders(): List<ZusatzHeader> =
        decodeHeaders(LumaticaApiManager.call("$API/headers"))

    suspend fun addHeaders(headers: List<NeuerHeader>): List<ZusatzHeader> =
        decodeHeaders(postJson("$API/headers-add-batch", buildJsonObject {
            put("headers", buildJsonArray { headers.forEach { header -> add(buildJsonObject {
                put("label", header.label)
                put("typ", header.typ)
            }) } })
        }.toString()))

    suspend fun updateHeaders(headers: List<ZusatzHeader>): List<ZusatzHeader> =
        decodeHeaders(postJson("$API/headers-update-batch", buildJsonObject {
            put("headers", buildJsonArray { headers.forEach { header -> add(buildJsonObject {
                put("id", header.id)
                put("label", header.label)
                put("typ", header.typ)
            }) } })
        }.toString()))

    suspend fun deleteHeader(id: String): List<ZusatzHeader> =
        decodeHeaders(postJson("$API/headers-delete", buildJsonObject { put("id", id) }.toString()))

    suspend fun importCsv(csv: String): CsvImportErgebnis =
        json.decodeFromString(CsvImportErgebnis.serializer(), postJson("$API/import-csv", buildJsonObject {
            put("csv", csv)
        }.toString()))

    suspend fun setLeadTypeFlow(ids: List<String>, method: String, flowId: String): List<LeadTypEintrag> =
        decodeLeadtypen(postJson("$API/leadtype-flow-set", buildJsonObject {
            put("leadTypIds", buildJsonArray { ids.forEach { add(it) } }); put("methode", method); put("flowProfileId", flowId)
        }.toString()))

    suspend fun loadLeadtypen(): List<LeadTypEintrag> =
        json.decodeFromString(ListSerializer(LeadTypEintrag.serializer()), LumaticaApiManager.call("$API/leadtypen"))

    suspend fun saveLeadtypen(entries: List<LeadTypEintrag>): List<LeadTypEintrag> =
        decodeLeadtypen(postJson("$API/leadtypen-save", buildJsonObject {
            put("leadtypen", buildJsonArray { entries.forEach { item -> add(buildJsonObject {
                put("id", item.id)
                put("leadTyp", item.leadTyp)
            }) } })
        }.toString()))

    suspend fun deleteLeadtypen(ids: List<String>): List<LeadTypEintrag> =
        decodeLeadtypen(postJson("$API/leadtypen-delete", buildJsonObject {
            put("ids", buildJsonArray { ids.forEach { add(it) } })
        }.toString()))

    private fun decodeLeadtypen(raw: String): List<LeadTypEintrag> =
        json.decodeFromString(ListSerializer(LeadTypEintrag.serializer()), raw)

    suspend fun loadIcpWerte(): List<IcpEintrag> =
        json.decodeFromString(ListSerializer(IcpEintrag.serializer()), LumaticaApiManager.call("$API/icp-werte"))

    suspend fun saveIcpWerte(entries: List<IcpEintrag>): List<IcpEintrag> =
        decodeIcpWerte(postJson("$API/icp-werte-save", buildJsonObject {
            put("werte", buildJsonArray { entries.forEach { item -> add(buildJsonObject {
                put("id", item.id)
                put("icp", item.icp)
            }) } })
        }.toString()))

    suspend fun deleteIcpWerte(ids: List<String>): List<IcpEintrag> =
        decodeIcpWerte(postJson("$API/icp-werte-delete", buildJsonObject {
            put("ids", buildJsonArray { ids.forEach { add(it) } })
        }.toString()))

    private fun decodeIcpWerte(raw: String): List<IcpEintrag> =
        json.decodeFromString(ListSerializer(IcpEintrag.serializer()), raw)

    private fun decodeHeaders(raw: String): List<ZusatzHeader> {
        if (raw.trimStart().startsWith("{")) error("Header-API auf diesem Node noch nicht verfügbar. Backend-Build aktualisieren.")
        return json.decodeFromString(ListSerializer(ZusatzHeader.serializer()), raw)
    }

    suspend fun loadEintraege(): List<All> =
        decodeList(LumaticaApiManager.call("$API/list"))

    suspend fun addInfo(id: String, text: String): All? = decodeOne(postJson("$API/info-add", buildJsonObject {
        put("id", id); put("leadInfoText", text)
    }.toString()))

    suspend fun deleteInfo(id: String, infoId: String): All? = decodeOne(postJson("$API/info-delete", buildJsonObject {
        put("id", id); put("leadInfoId", infoId)
    }.toString()))

    suspend fun searchEintraege(filters: Map<String, String>): List<All> {
        return decodeList(loadWithTabularFilters("$API/list", filters))
    }

    suspend fun searchLeaduebersicht(filters: Map<String, String>, linkedIn: Boolean = false): List<All> =
        decodeList(loadWithTabularFilters("$API/${if (linkedIn) "linkedin-leaduebersicht" else "leaduebersicht"}", filters))

    suspend fun searchMeineLeads(filters: Map<String, String>): List<All> =
        decodeList(loadWithTabularFilters("$API/meine-leads", filters))

    suspend fun returnMeineLeadsToPool(ids: List<String>) {
        val leads = decodeList(postJson("$API/meine-leads-pool-return", json.encodeToString(mapOf("ids" to ids))))
        check(leads.map { it.id }.toSet() == ids.toSet() && leads.all {
            it.status == "Vorbereitet" && it.akquiseZugewiesenAnId.isBlank()
        })
    }

    suspend fun saveAkquiseEintrag(eintrag: All): All? = decodeOne(postJson("$API/save-akquise-json", buildJsonObject {
        put("id", eintrag.id)
        put("akquiseDetails", buildJsonArray { eintrag.akquiseDetails.forEach { detail -> add(buildJsonObject {
            put("akquiseDetailDefinitionId", detail.akquiseDetailDefinitionId)
            put("akquiseDetailBezeichnung", detail.akquiseDetailBezeichnung)
            put("akquiseDetailAusgewaehlt", detail.akquiseDetailAusgewaehlt)
            put("akquiseDetailText", detail.akquiseDetailText)
        }) } })
        put("akquiseNotizen", eintrag.akquiseNotizen)
        put("akquiseDiagnose", eintrag.akquiseDiagnose)
        put("akquiseNaechsterMove", eintrag.akquiseNaechsterMove)
        put("akquiseStatus", eintrag.akquiseStatus)
        put("akquiseAbschlussAm", eintrag.akquiseAbschlussAm)
        put("akquiseCallAm", eintrag.akquiseCallAm)
        put("akquiseFollowUpAm", eintrag.akquiseFollowUpAm)
        put("akquiseTerminId", eintrag.akquiseTerminId)
        put("akquiseTerminAm", eintrag.akquiseTerminAm)
        put("akquiseCallTerminId", eintrag.akquiseCallTerminId)
        put("akquiseAbschlussNotiz", eintrag.akquiseAbschlussNotiz)
        put("akquiseErgebnis", eintrag.akquiseErgebnis)
        put("akquiseAktiverSchritt", eintrag.akquiseAktiverSchritt)
        put("akquiseCallLog", buildJsonArray { eintrag.akquiseCallLog.forEach { event -> add(buildJsonObject {
            put("akquiseLogTyp", event.akquiseLogTyp)
            put("akquiseLogWert", event.akquiseLogWert)
            put("akquiseLogAm", event.akquiseLogAm)
        }) } })
    }.toString()))

    suspend fun saveTabellenEintrag(eintrag: All): All? = decodeOne(postJson("$API/save-table-json", buildJsonObject {
        put("id", eintrag.id)
        put("firma", eintrag.firma)
        put("strasse", eintrag.strasse)
        put("hausnummer", eintrag.hausnummer)
        put("plz", eintrag.plz)
        put("ort", eintrag.ort)
        put("webseite", eintrag.webseite)
        put("linkedin", eintrag.linkedin)
        put("email", eintrag.email)
        put("ansprechpartner", eintrag.ansprechpartner)
        put("ansprechpartnerPosition", eintrag.ansprechpartnerPosition)
        put("telefon", eintrag.telefon)
        put("mobil", eintrag.mobil)
        put("vertriebZusatzfelder", buildJsonObject {
            eintrag.vertriebZusatzfelder.forEach { (id, value) -> put(id, value) }
        })
    }.toString()))

    suspend fun deleteEintraege(ids: List<String>) {
        postJson("$API/delete-json", json.encodeToString(mapOf("ids" to ids)))
    }

    suspend fun cancelFirmen(ids: List<String>) {
        val raw = postJson("$API/cancel-firmen", json.encodeToString(mapOf("ids" to ids)))
        val result = json.decodeFromString(ListSerializer(StatusAntwort.serializer()), raw)
        check(result.map { it.id }.toSet() == ids.toSet() && result.all { it.status == "Storniert" })
    }

    suspend fun setIcp(ids: List<String>, icp: String): List<All> {
        val raw = postJson("$API/icp-set", buildJsonObject {
            put("ids", buildJsonArray { ids.forEach { add(it) } })
            put("icp", icp)
        }.toString())
        val result = json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw)
        check(result.map { it.id }.toSet() == ids.toSet() && result.all { it.icp == icp })
        return result
    }

    suspend fun setLeadTyp(ids: List<String>, leadTyp: String, bereich: String = "INDEX"): List<All> {
        val raw = postJson("$API/lead-typ-set", buildJsonObject {
            put("ids", buildJsonArray { ids.forEach { add(it) } })
            put("leadTyp", leadTyp)
            put("bereich", bereich)
        }.toString())
        val result = json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw)
        check(result.map { it.id }.toSet() == ids.toSet() && result.all { it.leadTyp == leadTyp })
        return result
    }

    suspend fun addToVertriebspool(ids: List<String>) {
        val raw = postJson("$API/vertriebspool-add", json.encodeToString(mapOf("ids" to ids)))
        val result = json.decodeFromString(ListSerializer(StatusAntwort.serializer()), raw)
        check(result.map { it.id }.toSet() == ids.toSet() && result.all { it.status == "Vorbereitet" })
    }

    suspend fun assignLeadsToSelf(ids: List<String>): List<All> {
        val leads = decodeList(postJson("$API/vertriebspool-assign", buildJsonObject {
            put("ids", buildJsonArray { ids.forEach { add(it) } })
        }.toString()))
        check(leads.map { it.id }.toSet() == ids.toSet() && leads.all {
            it.status == "Meine Leads" && it.akquiseZugewiesenAnId == org.example.project.service.LoginService.getId()
        })
        return leads
    }

    suspend fun returnFromVertriebspool(ids: List<String>) {
        val raw = postJson("$API/vertriebspool-return", json.encodeToString(mapOf("ids" to ids)))
        val result = json.decodeFromString(ListSerializer(StatusAntwort.serializer()), raw)
        check(result.map { it.id }.toSet() == ids.toSet() && result.all { it.status == "Angelegt" })
    }

    private fun decodeOne(raw: String): All? =
        runCatching { json.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()

    private fun decodeList(raw: String): List<All> =
        json.decodeFromString(ListSerializer(All.AllNames.serializer()), raw)

    private suspend fun postJson(endpoint: String, body: String): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$endpoint",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = body
            )
        ).await()
        if (!response.ok) {
            val detail = runCatching { response.text().await() }.getOrDefault("")
            error("Vertriebsindex HTTP ${response.status}: $detail")
        }
        val result = response.text().await()
        return result
    }
}
